/**
 * Automatic rendering quality adaptation based on scene complexity.
 *
 * Monitors total polygon face count across blend-file meshes, imported OBJs,
 * and mannequins. When the scene exceeds complexity thresholds, rendering
 * parameters are silently scaled back to maintain smooth interaction:
 *
 * - **Full** (< 100 k faces): user-configured DPR/shadow limits apply as-is.
 * - **Medium** (100 k – 500 k): DPR and shadow maps reduced below user limits.
 * - **Low** (> 500 k): most aggressive reductions, AO also disabled.
 *
 * The user can configure upper bounds for DPR and shadow map resolution via
 * Settings → Viewport → Performance. The adaptive system will never exceed
 * those limits but may reduce further for heavy scenes.
 */

import { useMemo } from "react";
import { useViewportQuality } from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import { topologyFaceCount } from "../modeling/topology.ts";

export type QualityTier = "full" | "medium" | "low";

/** Polygon face count at which we begin reducing quality. */
const MEDIUM_THRESHOLD = 100_000;
/** Polygon face count at which we apply the most aggressive reductions. */
const LOW_THRESHOLD = 500_000;

export interface AdaptiveQuality {
  /** Current quality tier. */
  tier: QualityTier;
  /** Total polygon face count across the scene. */
  faceCount: number;
  /** Maximum device-pixel-ratio — heavy scenes render fewer pixels. */
  maxDpr: number;
  /** Shadow map resolution for directional/point lights. */
  shadowMapSize: number;
  /** Whether screen-space AO is allowed at this complexity level. */
  allowAo: boolean;
}

export function useAdaptivePerformance(): AdaptiveQuality {
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const userMaxDpr = useViewportQuality((q) => q.maxViewportDpr);
  const userShadowMapRes = useViewportQuality((q) => q.shadowMapResolution);

  return useMemo(() => {
    let faces = 0;

    for (const entry of blendFiles.values()) {
      for (const mesh of entry.file.meshes.values()) {
        faces += mesh.polyFaceCount;
      }
    }

    for (const obj of sceneObjects.values()) {
      if (obj.topology) {
        faces += topologyFaceCount(obj.topology);
      } else {
        // Fallback: estimate triangles from index buffer
        faces += Math.floor(obj.indices.length / 3);
      }
    }

    if (faces >= LOW_THRESHOLD) {
      return {
        tier: "low" as const,
        faceCount: faces,
        maxDpr: Math.min(userMaxDpr, 1),
        shadowMapSize: Math.min(userShadowMapRes, 512),
        allowAo: false,
      };
    }

    if (faces >= MEDIUM_THRESHOLD) {
      return {
        tier: "medium" as const,
        faceCount: faces,
        maxDpr: Math.min(userMaxDpr, 1.5),
        shadowMapSize: Math.min(userShadowMapRes, 512),
        allowAo: true,
      };
    }

    return {
      tier: "full" as const,
      faceCount: faces,
      maxDpr: userMaxDpr,
      shadowMapSize: userShadowMapRes,
      allowAo: true,
    };
  }, [blendFiles, sceneObjects, userMaxDpr, userShadowMapRes]);
}
